import {
	BulkOperationType,
	getBulkOperationParams,
	getCurrentSorting,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils';
import {
	BULK_EDIT_TRANSACTIONS_FORM_FIELD,
	BulkUpdateTransactionsFormValues,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/BulkUpdateTransactionsDrawer/useBulkUpdateTransactionsDrawerController';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { buildExportFileName } from '@dealstack/frontend/shared/files/exportFileName.utils';
import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
	bulkDeleteTransactions as bulkDelete,
	bulkRestoreTransactionsRequest as bulkRestore,
	bulkUpdateTransactions as bulkUpdate,
	downloadTransactionsExcel,
} from '@dealstack/frontend/app/services/Transactions.service.ts';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { formatDateToBEIgnoreTimezone } from '@dealstack/shared/lib/format/dates/date';
import {
	TransactionBulkOperationParams,
	TransactionTableFilter,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/types.ts';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types.ts';
import { mapTableFilterToDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/utils.ts';

export const useTransactionsBulkAction = (
	refetchTransactions: () => void,
	refreshCapDetails: () => void,
	refreshEntitiesList: () => void,
	tableDataOperations: TableOperations<TransactionTableFilter>
) => {
	const tCapManage = useTranslationCapManage();
	const capManageFlowController = useContext(CapManageControllerContext);
	const { capForceDeleteFlag, selectedEntityName } = capManageFlowController;
	const userIsAdmin = capForceDeleteFlag;

	const tableDataOperationsRef = useRef(tableDataOperations);

	useEffect(() => {
		tableDataOperationsRef.current = tableDataOperations;
	}, [tableDataOperations]);

	const deleteTransactionsRequest = useMutation({
		mutationFn: bulkDelete,
	});
	const updateTransactionsRequest = useMutation({
		mutationFn: bulkUpdate,
	});
	const restoreTransactionRequest = useMutation({
		mutationFn: bulkRestore,
	});
	const downloadExcelRequest = useMutation({
		mutationFn: downloadTransactionsExcel,
	});

	const [bulkOperationParams, setBulkOperationParams] =
		useState<TransactionBulkOperationParams>();
	const [confirmBulkDeleteOpened, setConfirmBulkDeleteOpened] = useState<boolean>(false);
	const [bulkUpdateOpened, setBulkUpdateOpened] = useState<boolean>(false);
	const [bulkUpdateField, setBulkUpdateField] = useState<BULK_EDIT_TRANSACTIONS_FORM_FIELD>();
	const [isLoading, setIsLoading] = useState(false);
	const [isThereInvestmentWorkflow, setIsThereInvestmentWorkflow] = useState(false);
	const [tsaNumber, setTsaNumber] = useState(0);
	const [pairedLegsIncluded, setPairedLegsIncluded] = useState(false);
	const [triggerRestore, setTriggerRestore] = useState(false);

	const downloadExcel = async (params: TransactionBulkOperationParams) => {
		if (downloadExcelRequest.isPending) {
			return;
		}
		setIsLoading(true);
		const operationParams = getBulkOperationParams(params);
		const selectedIds = operationParams.ids ?? [];
		const excludedIds = operationParams.excludedIds ?? [];
		const dataOperations = tableDataOperationsRef.current;

		const isFilteredOperation = operationParams.operationType === BulkOperationType.filtered;
		const sortBy = getCurrentSorting(params.table, dataOperations);

		downloadExcelRequest.mutate(
			{
				selectedIds: selectedIds.length ? selectedIds : undefined,
				excludedIds: isFilteredOperation && excludedIds.length ? excludedIds : undefined,
				personId: operationParams.personId || '',
				filter: mapTableFilterToDto(dataOperations.filter),
				search: dataOperations?.search,
				sortBy,
			},
			{
				onSuccess: (response) => {
					setIsLoading(false);
					downloadFileFromBlob(
						response,
						buildExportFileName('Transactions', selectedEntityName, new Date(), 'xlsx')
					);
				},
				onError: () => {
					setIsLoading(false);
					showToastV2({
						state: 'error',
						message: tCapManage('toasts.errors.failed_download_transaction'),
					});
				},
			}
		);
	};

	const bulkDeleteTransactions = async (forceDelete: boolean) => {
		if (!bulkOperationParams) {
			return;
		}

		const operationParams = getBulkOperationParams(bulkOperationParams);
		const dataOperations = tableDataOperationsRef.current;
		const selectedIds = operationParams.ids || [];
		const excludedIds = operationParams.excludedIds || [];
		const isFilteredOperation = operationParams.operationType === BulkOperationType.filtered;
		const sortBy = getCurrentSorting(bulkOperationParams.table, dataOperations);

		deleteTransactionsRequest.mutate(
			{
				selectedIds: selectedIds.length ? selectedIds : undefined,
				excludedIds: isFilteredOperation && excludedIds.length ? excludedIds : undefined,
				personId: operationParams.personId || '',
				forceDelete,
				// Only include filter/search/sortBy when not using selectedIds
				filter: mapTableFilterToDto(dataOperations?.filter),
				search: dataOperations?.search,
				sortBy,
			},
			{
				onSuccess: (resp) => {
					setBulkOperationParams(undefined);
					refetchTransactions();
					refreshCapDetails();
					refreshEntitiesList();
					showToastV2({
						message: `${resp?.count} ${tCapManage('toasts.errors.transactions_deleted_success')}`,
					});
				},
			}
		);
	};

	const bulkUpdateTransactions = async (formValues: BulkUpdateTransactionsFormValues) => {
		if (!bulkOperationParams || !bulkUpdateField || updateTransactionsRequest.isPending) {
			return;
		}

		const operationParams = getBulkOperationParams(bulkOperationParams);
		const dataOperations = tableDataOperationsRef.current;
		const selectedIds = operationParams.ids ?? [];
		const excludedIds = operationParams.excludedIds ?? [];

		const isFilteredOperation = operationParams.operationType === BulkOperationType.filtered;
		const sortBy = getCurrentSorting(bulkOperationParams.table, dataOperations);
		const date = formValues[BULK_EDIT_TRANSACTIONS_FORM_FIELD.DATE]
			? formatDateToBEIgnoreTimezone(formValues[BULK_EDIT_TRANSACTIONS_FORM_FIELD.DATE])
			: undefined;

		updateTransactionsRequest.mutate(
			{
				selectedIds: selectedIds.length ? selectedIds : undefined,
				excludedIds: isFilteredOperation && excludedIds.length ? excludedIds : undefined,
				personId: operationParams.personId ?? '',
				// Only include filter/search/sortBy when not using selectedIds
				filter: mapTableFilterToDto(dataOperations?.filter),
				search: dataOperations?.search,
				sortBy,
				// Spread the form values (type, sourceId, destinationId, etc.)
				[bulkUpdateField]: formValues[bulkUpdateField] ?? undefined,
				date,
			},
			{
				onSuccess: (resp) => {
					setBulkOperationParams(undefined);
					setBulkUpdateField(undefined);
					refetchTransactions();
					refreshCapDetails();
					refreshEntitiesList();
					showToastV2({
						message: `${resp?.count} ${tCapManage('toasts.errors.transactions_update_success')}`,
					});
				},
			}
		);
	};

	const bulkRestoreTransactions = async (payload: TransactionBulkOperationParams) => {
		if (!bulkOperationParams || !payload || restoreTransactionRequest.isPending) {
			return;
		}

		const operationParams = getBulkOperationParams(bulkOperationParams);
		const dataOperations = tableDataOperationsRef.current;
		const selectedIds = operationParams.ids || [];
		const excludedIds = operationParams.excludedIds || [];
		const isFilteredOperation = operationParams.operationType === BulkOperationType.filtered;
		const sortBy = getCurrentSorting(bulkOperationParams.table, dataOperations);

		restoreTransactionRequest.mutate(
			{
				selectedIds: selectedIds.length ? selectedIds : undefined,
				excludedIds: isFilteredOperation && excludedIds.length ? excludedIds : undefined,
				personId: operationParams.personId || '',
				deleted: false,
				// Only include filter/search/sortBy when not using selectedIds
				filter: mapTableFilterToDto(dataOperations?.filter),
				search: dataOperations?.search,
				sortBy,
			},
			{
				onSuccess: (resp) => {
					if (bulkOperationParams?.table) {
						const meta = getTableMeta(bulkOperationParams.table);
						meta.resetActionState?.();
					}
					setBulkOperationParams(undefined);
					refetchTransactions();
					refreshCapDetails();
					refreshEntitiesList();
					showToastV2({
						message: `${resp?.count} ${tCapManage('toasts.errors.transactions_restore_success')}`,
					});
				},
				// CAP-2039: a per-call onError is ADDITIVE to ApiLayer's default, so this raised two
				// toasts - a generic one and the code-derived one - and the generic one is the one
				// that read as the answer. Dropped: the default already says why the restore was
				// refused, and bulk restore now enforces the same rules the single path does.
			}
		);
	};

	const initiateBulkDelete = (payload: TransactionBulkOperationParams) => {
		setBulkOperationParams(payload);
		const operationParams = getBulkOperationParams(payload);

		setBulkOperationParams(payload);
		setIsThereInvestmentWorkflow(operationParams.investmentWorkflowIncluded || false);
		setTsaNumber(operationParams.tsaCount || 0);
		setPairedLegsIncluded(operationParams.pairedLegsIncluded || false);
		setConfirmBulkDeleteOpened(true);
	};

	const initiateBulkUpdate = (
		payload: TransactionBulkOperationParams,
		updateField: BULK_EDIT_TRANSACTIONS_FORM_FIELD
	) => {
		setBulkUpdateField(updateField);
		setBulkOperationParams(payload);
		setBulkUpdateOpened(true);
	};

	const initiateBulkRestore = (payload: TransactionBulkOperationParams) => {
		setBulkOperationParams(payload);
		setTriggerRestore(true);
	};

	useEffect(() => {
		if (triggerRestore && bulkOperationParams) {
			void bulkRestoreTransactions(bulkOperationParams);
			bulkOperationParams?.table?.resetRowSelection();
			setTriggerRestore(false);
		}
	}, [triggerRestore, bulkOperationParams]);

	return {
		downloadExcel,
		bulkDeleteTransactions,
		deleteTransactionsRequest,
		isLoading,
		confirmBulkDeleteOpened,
		setConfirmBulkDeleteOpened,
		bulkUpdateOpened,
		setBulkUpdateOpened,
		bulkUpdateField,
		initiateBulkDelete,
		initiateBulkUpdate,
		initiateBulkRestore,
		bulkUpdateTransactions,
		updateTransactionsRequest,
		bulkOperationParams,
		isThereInvestmentWorkflow,
		tsaNumber,
		pairedLegsIncluded,
		userIsAdmin,
	};
};
